import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { GraduationCap, ArrowLeft, MessageSquarePlus, MapPin } from "lucide-react";
import { toast } from "sonner";

export function TutorPortal() {
  const [rows, setRows] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/tutor/assignments").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  if (!rows) return <Skeleton className="h-64 w-full rounded-2xl" />;
  return (
    <div className="space-y-6" data-testid="tutor-portal">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Tutor portal</h1>
        <p className="mt-1 text-muted-foreground">Assignments your students have shared with you.</p>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card py-16 text-center text-muted-foreground" data-testid="tutor-empty">
          Your students haven't shared an assignment yet.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((a) => (
            <button key={a.id} onClick={() => navigate(`/app/tutor/${a.id}`)} data-testid={`tutor-assignment-${a.id}`}
              className="rounded-2xl border border-border bg-card p-6 text-left transition-colors duration-200 hover:border-[#0B5D3B]/40">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E8F5EE] text-[#0B5D3B] dark:bg-emerald-950 dark:text-[#25D366]"><GraduationCap className="h-5 w-5" /></div>
              <h3 className="mt-4 font-display text-lg font-semibold">{a.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{a.student_name} · {a.study_country}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function TutorAssignment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [comment, setComment] = useState({ paragraph_id: "", text: "" });

  const load = () => api.get(`/tutor/assignments/${id}`).then(({ data }) => setA(data)).catch(() => navigate("/app/tutor"));
  useEffect(() => { load(); }, [id]);

  const addComment = async () => {
    if (!comment.text.trim()) return toast.error("Write a comment first.");
    try {
      await api.post("/tutor/comments", { assignment_id: id, paragraph_id: comment.paragraph_id || null, verdict: "adjust", text: comment.text });
      toast.success("Comment added.");
      setComment({ paragraph_id: "", text: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!a) return <Skeleton className="h-96 w-full rounded-2xl" />;
  const ev = a.evaluations?.[0]?.json;

  return (
    <div className="space-y-6" data-testid="tutor-assignment">
      <button onClick={() => navigate("/app/tutor")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" />Back</button>
      <h1 className="font-display text-2xl font-bold">{a.title}</h1>

      {a.blueprint && (
        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="mb-2 font-display font-semibold">Blueprint summary</h3>
          <p className="text-sm text-muted-foreground">{a.blueprint.summary}</p>
        </div>
      )}
      {ev && (
        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="mb-2 font-display font-semibold">Latest evaluation</h3>
          <p className="text-sm">Band: <strong>{ev.rubric_band}</strong> · {ev.grade_label}</p>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="mb-3 font-display font-semibold">Student draft (read-only)</h3>
          <div className="space-y-3">
            {a.draft_paragraphs?.map((p) => (
              <div key={p.id} className="rounded-lg px-3 py-2 text-sm hover:bg-accent/60">
                <span className="mr-2 font-mono text-xs font-semibold text-[#0B5D3B] dark:text-[#25D366]">[{p.id}]</span>{p.text}
              </div>
            )) || <p className="text-sm text-muted-foreground">No draft shared.</p>}
          </div>
        </div>
        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="mb-3 font-display font-semibold">Add a comment</h3>
            <input value={comment.paragraph_id} onChange={(e) => setComment({ ...comment, paragraph_id: e.target.value })}
              placeholder="Paragraph id (e.g. P2), optional" className="mb-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="tutor-comment-para" />
            <Textarea rows={3} value={comment.text} onChange={(e) => setComment({ ...comment, text: e.target.value })} placeholder="Your guidance (you never rewrite the student's work)" data-testid="tutor-comment-text" />
            <Button onClick={addComment} className="mt-3 bg-[#0B5D3B] text-white hover:bg-[#08472D]" data-testid="tutor-comment-submit"><MessageSquarePlus className="mr-1.5 h-4 w-4" />Post comment</Button>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="mb-3 font-display font-semibold">Your comments</h3>
            {a.tutor_comments?.length ? a.tutor_comments.map((c) => (
              <div key={c.id} className="border-b border-border py-2 text-sm last:border-0" data-testid={`tutor-comment-${c.id}`}>
                {c.paragraph_id && <span className="mr-2 inline-flex items-center gap-1 rounded bg-accent px-2 py-0.5 font-mono text-xs"><MapPin className="h-3 w-3" />{c.paragraph_id}</span>}
                {c.text}
              </div>
            )) : <p className="text-sm text-muted-foreground">No comments yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
